import { useEffect, useMemo, useState } from "react"
import { BarChart3, Clock3, Loader2, RefreshCw, Search, TrendingUp, Youtube } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { getSearchAnalytics } from "@/lib/api"

const periods = [
  { value: "lifetime", label: "Lifetime" },
  { value: "today", label: "Today" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
]

const metricCards = [
  {
    key: "cacheHits",
    label: "Cache Hits",
    icon: TrendingUp,
    tone: "emerald",
    description: "Searches served from local catalog",
  },
  {
    key: "cacheMisses",
    label: "Cache Misses",
    icon: Search,
    tone: "slate",
    description: "Searches requiring fallback",
  },
  {
    key: "hitRate",
    label: "Hit Rate",
    icon: BarChart3,
    tone: "emerald",
    percent: true,
    description: "Cache hits divided by total searches",
  },
  {
    key: "youtubeSearches",
    label: "YouTube Searches",
    icon: Youtube,
    tone: "purple",
    description: "Fallback and explicit YouTube searches",
  },
  {
    key: "avgSearchTimeMs",
    label: "Average Search Time",
    icon: Clock3,
    tone: "purple",
    ms: true,
    description: "Average across local and YouTube endpoints",
  },
]

const metricTone = {
  purple: "border-purple-500/30 bg-purple-500/10 text-purple-200",
  emerald: "border-emerald-500/30 bg-emerald-500/10 text-emerald-200",
  slate: "border-white/10 bg-white/5 text-zinc-200",
}

function formatMetric(card, value) {
  if (value === null || value === undefined || value === "") return "Coming Soon"
  if (card.percent) return `${Math.round(Number(value) * 1000) / 10}%`
  if (card.ms) return `${Math.round(Number(value) || 0)} ms`
  if (typeof value === "number") return value.toLocaleString()
  return String(value)
}

export default function SearchAnalyticsPage() {
  const [period, setPeriod] = useState("lifetime")
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [analytics, setAnalytics] = useState(null)

  const load = async (nextPeriod = period) => {
    setLoading(true)
    try {
      const data = await getSearchAnalytics({ limit: 10, period: nextPeriod })
      setAnalytics(data)
      setError(false)
    } catch {
      setAnalytics(null)
      setError(true)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load(period)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period])

  const cards = useMemo(
    () =>
      metricCards.map((card) => ({
        ...card,
        value: analytics ? analytics[card.key] : null,
      })),
    [analytics]
  )

  return (
    <div className="space-y-6" data-testid="developer-search-page">
      <Card className="glass border-white/5 overflow-hidden">
        <CardContent className="p-6 sm:p-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-purple-300 mb-2">Developer Console</div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white font-[Outfit] leading-tight">Search Analytics</h2>
            <p className="text-zinc-400 mt-2 max-w-2xl">
              Search behavior and cache efficiency from existing analytics collections using MongoDB aggregations.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select
              className="input-base"
              value={period}
              onChange={(event) => setPeriod(event.target.value)}
              data-testid="search-analytics-period"
            >
              {periods.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
            <Button
              type="button"
              variant="outline"
              className="border-white/10 bg-white/5 text-zinc-200 hover:bg-white/10"
              onClick={() => load(period)}
              disabled={loading}
              data-testid="search-analytics-refresh"
            >
              {loading ? <Loader2 className="animate-spin" size={16} /> : <RefreshCw size={16} />}
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {cards.map((card) => {
          const value = loading ? "Loading..." : formatMetric(card, error ? null : card.value)
          return (
            <Card key={card.key} className="glass border-white/5 hover:border-purple-500/30 transition-colors" data-testid={`metric-card-${card.key}`}>
              <CardContent className="p-5 sm:p-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">{card.label}</div>
                    <div className="text-3xl sm:text-4xl font-bold text-white font-[Outfit] leading-none">{value}</div>
                  </div>
                  <div className={`w-11 h-11 rounded-2xl border flex items-center justify-center ${metricTone[card.tone]}`}>
                    <card.icon size={18} strokeWidth={2.3} />
                  </div>
                </div>
                <div className="mt-4 text-sm text-zinc-400">{error ? "Coming Soon" : card.description}</div>
              </CardContent>
            </Card>
          )
        })}
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <Card className="glass border-white/5" data-testid="top-list-most-searched-terms">
          <CardHeader className="p-6 pb-3">
            <CardTitle className="text-sm uppercase tracking-[0.25em] text-zinc-500">Most Searched Terms</CardTitle>
          </CardHeader>
          <CardContent className="p-6 pt-0">
            {loading ? (
              <div className="h-40 flex items-center justify-center text-zinc-400 gap-2">
                <Loader2 className="animate-spin" size={16} />
                Loading terms...
              </div>
            ) : !analytics?.mostSearchedTerms?.length ? (
              <div className="h-40 flex items-center justify-center text-zinc-500">No search terms available</div>
            ) : (
              <div className="space-y-2">
                {analytics.mostSearchedTerms.map((term, index) => (
                  <div
                    key={`${term.term}-${index}`}
                    className="rounded-xl border border-white/10 bg-zinc-900/50 p-3 flex items-center justify-between gap-3"
                    data-testid={`most-searched-term-${index}`}
                  >
                    <div className="min-w-0">
                      <div className="text-white font-medium truncate">{term.term}</div>
                    </div>
                    <Badge variant="outline" className="border-white/10 bg-white/5 text-zinc-200">
                      {Number(term.count || 0).toLocaleString()}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="glass border-white/5" data-testid="top-list-missing-songs">
          <CardHeader className="p-6 pb-3">
            <CardTitle className="text-sm uppercase tracking-[0.25em] text-zinc-500">Most Requested Missing Songs</CardTitle>
          </CardHeader>
          <CardContent className="p-6 pt-0">
            {loading ? (
              <div className="h-40 flex items-center justify-center text-zinc-400 gap-2">
                <Loader2 className="animate-spin" size={16} />
                Loading missing songs...
              </div>
            ) : !analytics?.mostRequestedMissingSongs?.length ? (
              <div className="h-40 flex items-center justify-center text-zinc-500">No missing-song signals yet</div>
            ) : (
              <div className="space-y-2">
                {analytics.mostRequestedMissingSongs.map((term, index) => (
                  <div
                    key={`${term.term}-${index}`}
                    className="rounded-xl border border-white/10 bg-zinc-900/50 p-3 flex items-center justify-between gap-3"
                    data-testid={`missing-song-term-${index}`}
                  >
                    <div className="min-w-0">
                      <div className="text-white font-medium truncate">{term.term}</div>
                    </div>
                    <Badge variant="outline" className="border-amber-500/30 bg-amber-500/10 text-amber-200">
                      {Number(term.count || 0).toLocaleString()}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
